import type { ComponentProps, MutableRefObject, ReactNode } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type { FlexProps } from 'styled-system';
import { color, flex, height, space } from 'styled-system';

import type { SpaceProps } from 'common/theme/system-types';
import type { OverflowXProps, OverflowYProps } from '../../theme/system-utilities';
import { overflowX, overflowY } from '../../theme/system-utilities';
import type { ErrorMessage } from '../../types/error';
import { isErrorMessage } from '../../utils/error-handlers/error-message';
import { Flex } from '../Flex';
import DrawerFormAlert from './DrawerFormAlert';
import Footer from './Footer';

interface DrawerFormProps {
  error: Nullable<React.ReactElement<any> | ErrorMessage>;
  ctaLabel: string;
  isCtaActive: boolean;
  isCtaLoading: boolean;
  onSubmit: () => void;
  setBodyRef?: MutableRefObject<Nullable<HTMLElement>>;
  bodyStyles?: SpaceProps;
  backgroundColor?: string;
  errorStyles?: SpaceProps;
  children: ReactNode;
  secondaryBtnLabel?: string;
  secondaryBtnOnClick?: () => void;
  ariaCtaLabel?: string;
}

const UnstyledDrawerContainer = styled(Flex)<any>`
  ${height};
  ${color};
`;

const DrawerContainer = (props: ComponentProps<typeof UnstyledDrawerContainer>) => (
  <UnstyledDrawerContainer flexDirection="column" height="100%" {...props} />
);

const UnstyledContainer = styled.div<SpaceProps>`
  ${space};
`;

const Container = (props: ComponentProps<typeof UnstyledContainer>) => (
  <UnstyledContainer
    py={['sp_16', null, null, 'sp_24']}
    px={['sp_16', null, null, 'sp_24']}
    {...props}
  />
);

const UnstyledBody = styled.article<FlexProps & OverflowXProps & OverflowYProps>`
  ${flex};
  ${overflowY};
  ${overflowX};
`;

const Body = (props: ComponentProps<typeof UnstyledBody>) => (
  <UnstyledBody flex="1 1 auto" overflowY="auto" overflowX="hidden" {...props} />
);

const DrawerForm = ({
  error,
  children,
  ctaLabel,
  isCtaActive,
  isCtaLoading,
  onSubmit,
  setBodyRef,
  bodyStyles,
  backgroundColor,
  errorStyles,
  secondaryBtnLabel,
  secondaryBtnOnClick,
  ariaCtaLabel,
}: DrawerFormProps) => (
  <DrawerContainer backgroundColor={backgroundColor}>
    <Body ref={setBodyRef}>
      {isErrorMessage(error) ? <DrawerFormAlert message={error} {...errorStyles} /> : error}
      <Container {...bodyStyles}>{children}</Container>
    </Body>
    <Footer
      label={ctaLabel}
      isLoading={isCtaLoading}
      isActive={isCtaActive}
      onClick={onSubmit}
      secondaryBtnOnClick={secondaryBtnOnClick}
      secondaryBtnLabel={secondaryBtnLabel}
      ariaCtaLabel={ariaCtaLabel}
    />
  </DrawerContainer>
);

export default DrawerForm;
